import { FilterOutlined } from "@ant-design/icons"
import { Avatar, Button, Flex, Tooltip } from "antd"
import React, { memo, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { DateRangeFilter } from "../../../../../components/Filters/DateRangeFilter.tsx"
import { ResolvedSelect } from "../../../../../components/Select/ResolvedSelect.tsx"
import {
  LEADS_ADVANCED_FILTER_KEYS,
  LEADS_FILTER_KEYS,
  clearedActiveFilters,
  clearedFilters,
  countActiveFilters,
  normalizeFilterValue
} from "../../../redux/leads.constants.ts"
import type {
  LeadsFilterOptions,
  LeadsFilters
} from "../../../redux/leads.types.ts"
import { groupLeadStatusOptions } from "../../../constants/group-lead-status-options.ts"
import { withNoneSourceOption } from "../../../constants/lead-source-none.ts"
import { LeadFilterField } from "./LeadFilterField.tsx"
import { LeadListTableMoreFilters } from "./LeadListTableMoreFilters.tsx"

interface LeadListTableFiltersProps {
  filters: LeadsFilters
  isLocked?: boolean
  filterOptions: LeadsFilterOptions
  hideSalesFilter?: boolean
  showConvertedAllowed?: boolean
  onFilersChange: (data: Partial<LeadsFilters>) => void
}

export const LeadListTableFilters = memo(
  ({
    filters,
    filterOptions,
    isLocked = false,
    onFilersChange,
    hideSalesFilter,
    showConvertedAllowed
  }: LeadListTableFiltersProps) => {
    const { t } = useTranslation()
    const [moreOpen, setMoreOpen] = useState(false)
    const [resetToken, setResetToken] = useState(0)

    const onFilterChange = (key: keyof LeadsFilters) => (value: unknown) => {
      onFilersChange({ [key]: normalizeFilterValue(value) })
    }

    // Both reset paths live here because the drawer stays mounted once opened,
    // so Clear All reaches debounced fields the user can no longer see.
    const resetFilters = (values: Partial<LeadsFilters>) => {
      onFilersChange(values)
      setResetToken((token) => token + 1)
    }

    // filter_options carries only id/first_name/last_name for sales managers —
    // no avatar URL — so the chip is drawn from the initial rather than a photo.
    // The node is shared by the dropdown row and the selected chip, which is
    // what puts the owner faces inside the closed control as the design has
    // them, so it stays compact enough to sit in a tag.
    const salesOptions = useMemo(
      () =>
        filterOptions?.["sales_manager"]?.map((el) => {
          const name = el?.label || (typeof el !== "object" ? el : "No Name")
          return {
            value: el?.value || (typeof el !== "object" ? el : ""),
            label: (
              <Flex align={"center"} gap={6}>
                <Avatar size={18} className={"shrink-0 text-[10px]"}>
                  {String(name).charAt(0)}
                </Avatar>
                <span className={"min-w-0 truncate"}>{name}</span>
              </Flex>
            ),
            // Searching and the collapsed "+N" chips both read this, which a
            // ReactNode label cannot serve.
            title: String(name)
          }
        }),
      [filterOptions]
    )

    const statusOptions = useMemo(
      () =>
        groupLeadStatusOptions(
          filterOptions?.["status"],
          t("leadsTable.filters.legacyStatuses")
        ),
      [filterOptions, t]
    )

    const sourceOptions = useMemo(
      () => withNoneSourceOption(filterOptions?.["source"]),
      [filterOptions]
    )

    const activeCount = countActiveFilters(filters, LEADS_FILTER_KEYS)
    const advancedCount = countActiveFilters(
      filters,
      LEADS_ADVANCED_FILTER_KEYS
    )

    return (
      <>
        <Flex
          wrap="wrap"
          gap="small"
          align="end"
          className="py-4 md:p-4 border-b border-gray-200"
        >
          <LeadFilterField
            label={t("leadsTable.filters.lifecycleStatus")}
            className={"max-md:basis-60 w-44 max-md:grow"}
          >
            <ResolvedSelect
              maxTagCount={"responsive"}
              mode={"multiple"}
              placeholder={t("leadsTable.filters.allStatuses")}
              value={filters?.status}
              onChange={onFilterChange("status")}
              allowClear
              options={statusOptions}
              disabled={isLocked}
            />
          </LeadFilterField>
          {/* Overlays match with __overlap, so picking a second one widens the
              result set — the opposite of every other filter in this row. */}
          <LeadFilterField
            label={t("leadsTable.filters.operationalState")}
            className={"basis-60 w-56 max-md:grow"}
          >
            <Tooltip title={t("leadsTable.filters.overlapHint")}>
              <ResolvedSelect
                maxTagCount={"responsive"}
                mode={"multiple"}
                placeholder={t("leadsTable.filters.anyOperationalState")}
                value={filters?.operational_states}
                onChange={onFilterChange("operational_states")}
                allowClear
                options={filterOptions?.["operational_states"]}
                disabled={isLocked}
              />
            </Tooltip>
          </LeadFilterField>
          <LeadFilterField
            label={t("leadsTable.filters.followUp")}
            className={"w-60 max-md:grow"}
          >
            <DateRangeFilter
              after={filters?.follow_up_date_after}
              before={filters?.follow_up_date_before}
              afterKey="follow_up_date_after"
              beforeKey="follow_up_date_before"
              disabled={isLocked}
              className={"w-full"}
              placeholder={t("leadsTable.filters.followUp")}
              onChange={onFilersChange}
            />
          </LeadFilterField>
          {!hideSalesFilter && (
            <LeadFilterField
              label={t("leadsTable.filters.owner")}
              className={"basis-60 w-56 max-md:grow"}
            >
              <ResolvedSelect
                maxTagCount="responsive"
                mode={"multiple"}
                placeholder={t("leadsTable.filters.allOwners")}
                value={filters?.sales_manager}
                onChange={onFilterChange("sales_manager")}
                allowClear
                showSearch
                optionFilterProp="title"
                options={salesOptions}
                disabled={isLocked}
              />
            </LeadFilterField>
          )}
          <LeadFilterField
            label={t("leadsTable.filters.source")}
            className={"basis-60 w-48 max-md:grow"}
          >
            <ResolvedSelect
              maxTagCount="responsive"
              mode={"multiple"}
              placeholder={t("leadsTable.filters.allSources")}
              value={filters?.source}
              onChange={onFilterChange("source")}
              allowClear
              options={sourceOptions}
              disabled={isLocked}
            />
          </LeadFilterField>
          {/* The label slot is empty so the controls line up on their tops. */}
          <LeadFilterField label={" "}>
            {/* The one accented control in the row, and the count sits inside
                it rather than floating off a corner — a filter already applied
                behind this button is the thing most worth noticing here. */}
            <Button
              icon={<FilterOutlined />}
              onClick={() => setMoreOpen(true)}
              disabled={isLocked}
              className={
                "border-blue-500 text-blue-600 hover:!border-blue-600 hover:!text-blue-700"
              }
            >
              {t("leadsTable.filters.moreFilters")}
              {!!advancedCount && (
                <span
                  data-testid="more-filters-count"
                  className={
                    "ml-1 inline-flex min-w-5 items-center justify-center rounded-full bg-blue-50 px-1.5 text-xs font-semibold text-blue-600"
                  }
                >
                  {advancedCount}
                </span>
              )}
            </Button>
          </LeadFilterField>
          <LeadFilterField label={" "}>
            <Button
              type={"link"}
              disabled={isLocked || !activeCount}
              onClick={() => resetFilters(clearedActiveFilters())}
            >
              {t("leadsTable.filters.clearAll")}
              {/* The slice is persisted, so a filter set on an earlier visit is
                  still in force on this one — including the few no control in
                  this row renders. The total says so before the empty table
                  has to. */}
              {!!activeCount && (
                <span
                  data-testid="active-filters-count"
                  aria-label={t("leadsTable.filters.activeCount", {
                    count: activeCount
                  })}
                  className={
                    "ml-1 inline-flex min-w-5 items-center justify-center rounded-full bg-blue-50 px-1.5 text-xs font-semibold text-blue-600"
                  }
                >
                  {activeCount}
                </span>
              )}
            </Button>
          </LeadFilterField>
        </Flex>

        <LeadListTableMoreFilters
          open={moreOpen}
          filters={filters}
          filterOptions={filterOptions}
          showConvertedAllowed={showConvertedAllowed}
          resetSignal={resetToken}
          onClose={() => setMoreOpen(false)}
          onFilersChange={onFilersChange}
          onReset={() =>
            resetFilters(clearedFilters(LEADS_ADVANCED_FILTER_KEYS))
          }
        />
      </>
    )
  }
)

LeadListTableFilters.displayName = "LeadListTableFilters"
